import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import partyLogoMap from "../utils/partyLogoMap";
import RepresentativePopupCard from "./RepresentativePopupCard";

const RepresentativeCard = ({ representative, onClick }) => {
  const logo = partyLogoMap[representative.party] || "/party-logo.png";

  return (
    <button
      type="button"
      className="flex w-full items-start gap-3 rounded-2xl border bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
      onClick={onClick}
    >
      <img
        src={logo}
        alt={representative.party || "Party"}
        className="h-10 w-10 rounded-full object-contain"
        onError={(event) => {
          event.currentTarget.src = "/party-logo.png";
        }}
      />
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{representative.name}</div>
        <div className="truncate text-sm text-gray-600">{representative.constituency}</div>
        <div className="truncate text-xs text-gray-500">
          {representative.party || "Independent"}
        </div>
      </div>
    </button>
  );
};

const EmptyState = ({ label }) => (
  <div className="rounded-2xl border border-dashed bg-white/60 p-6 text-center text-sm text-gray-500">
    {label}
  </div>
);

const StateRepresentativeGrid = ({ mps = [], mlas = [] }) => {
  const [selectedRep, setSelectedRep] = useState(null);
  const navigate = useNavigate();

  const sortedMPs = [...mps].sort((a, b) =>
    (a.constituency || "").localeCompare(b.constituency || ""),
  );
  const sortedMLAs = [...mlas].sort((a, b) =>
    (a.constituency || "").localeCompare(b.constituency || ""),
  );

  const handleCardClick = (rep) => {
    if (rep?.name) {
      navigate(`/candidate/${encodeURIComponent(rep.name)}`);
      return;
    }

    setSelectedRep(rep);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h2 className="text-center text-2xl font-bold text-gray-900">
        Your State MP and MLA Representatives
      </h2>
      <p className="mt-2 text-center text-gray-600">
        Live counts come directly from the normalized API response.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-3xl bg-green-100/80 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-green-900">Members of Parliament</h3>
            <span className="rounded-full bg-green-600 px-3 py-1 text-sm text-white">
              {sortedMPs.length}
            </span>
          </div>
          <div className="grid max-h-[520px] grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2">
            {sortedMPs.length > 0 ? (
              sortedMPs.map((mp, index) => (
                <RepresentativeCard
                  key={mp._id || `${mp.constituency}-${mp.party}-${index}`}
                  representative={mp}
                  onClick={() => handleCardClick(mp)}
                />
              ))
            ) : (
              <EmptyState label="No MPs are available for this state yet." />
            )}
          </div>
        </div>

        <div className="rounded-3xl bg-indigo-100/80 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-indigo-900">
              Members of the Legislative Assembly
            </h3>
            <span className="rounded-full bg-indigo-600 px-3 py-1 text-sm text-white">
              {sortedMLAs.length}
            </span>
          </div>
          <div className="grid max-h-[520px] grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2">
            {sortedMLAs.length > 0 ? (
              sortedMLAs.map((mla, index) => (
                <RepresentativeCard
                  key={mla._id || `${mla.constituency}-${mla.party}-${index}`}
                  representative={mla}
                  onClick={() => handleCardClick(mla)}
                />
              ))
            ) : (
              <EmptyState label="No MLAs are available for this state yet." />
            )}
          </div>
        </div>
      </div>

      <RepresentativePopupCard
        open={Boolean(selectedRep)}
        rep={selectedRep}
        onClose={() => setSelectedRep(null)}
      />
    </div>
  );
};

export default StateRepresentativeGrid;
